import  {
  DOMUtil,
  Vector2,
} from '../rocket';

export interface PointerDragEventManagerConfig {
  target?: HTMLElement;
  offsetFrom?: HTMLElement;

  preventDefault: boolean;
  keepHistory: boolean;

  condition: (event: TouchEvent | MouseEvent, manager: PointerDragEventManager) => boolean;

  onEvent: (event: TouchEvent | MouseEvent, manager: PointerDragEventManager) => void;
  onStart: (pointerEvent: PointerDragEvent, manager: PointerDragEventManager) => void;
  onDrag: (pointerEvent: PointerDragEvent, manager: PointerDragEventManager) => void;
  onEnd: (pointerEvent: PointerDragEvent, manager: PointerDragEventManager) => void;
  onCancel: (pointerEvent: PointerDragEvent, manager: PointerDragEventManager) => void;
}

export const POINTER_DRAG_EVENT_MANAGER_DEFAULT_CONFIG: PointerDragEventManagerConfig = {
  target: undefined,
  offsetFrom: undefined,

  preventDefault: true,
  keepHistory: false,

  condition: () => true,

  onEvent: () => {},
  onStart: () => {},
  onDrag: () => {},
  onEnd: () => {},
  onCancel: () => {},
};

export type PointerDragEventType = 'down' | 'drag' | 'up' | 'cancel';

export interface PointerDragEvent {
  type: PointerDragEventType;
  event: TouchEvent | MouseEvent;
  target: EventTarget | null;

  isTouch: boolean;
  touch?: Touch;
  identifier?: number;

  pointerTargetOffset: Vector2;
  position: Vector2;
  velocity: Vector2;
  acceleration: Vector2;

  time: number;
}

export class PointerDragEventManager {
  public config: PointerDragEventManagerConfig;

  public isActive: boolean = false;
  public isTouch: boolean = false;

  public touchIdentifier: number = 0;

  public startTime: number = 0;

  public history: PointerDragEvent[];

  public startingPointerDragEvent?: PointerDragEvent;
  public previousPointerDragEvent?: PointerDragEvent;

  public pointerTargetOffset: Vector2;

  public previousPosition: Vector2;
  public previousVelocity: Vector2;

  constructor(config?: Partial<PointerDragEventManagerConfig>) {
    this.config = Object.assign({}, POINTER_DRAG_EVENT_MANAGER_DEFAULT_CONFIG);
    this.setConfig(config);

    this.pointerTargetOffset = new Vector2();
    this.previousPosition = new Vector2();
    this.previousVelocity = new Vector2();

    this.history = [];

    this.listen();
  }

  public setConfig(config?: Partial<PointerDragEventManagerConfig>): this {
    if (typeof config === 'object') {
      Object.assign(this.config, config);
    }

    return this;
  }

  private preventDefault(event: MouseEvent | TouchEvent) {
    if (this.config.preventDefault === true) {
      event.preventDefault();
    }
  }

  private updatePointerTargetOffset(x: number, y: number) {
    const { target, offsetFrom } = this.config;

    if (DOMUtil.isHTMLElement(target) === true) {
      let element = target as HTMLElement;

      if (DOMUtil.isHTMLElement(offsetFrom) === true) {
        element = offsetFrom as HTMLElement;
      }
        
      const { left, top } = element.getBoundingClientRect();

      this.pointerTargetOffset.equals(
        x - left,
        y - top,
      );
    }
  }

  private updateHistory(pointerEvent: PointerDragEvent) {
    const { keepHistory } = this.config;

    if (keepHistory === true) {
      if (
        typeof this.history !== 'object'
        || Array.isArray(this.history) === false
      ) {
        this.history = [];
      }

      this.history.push(pointerEvent);
    }
  }

  private createMousePointerDragEvent(type: PointerDragEventType, event: MouseEvent): PointerDragEvent {
    const { clientX, clientY, target } = event;

    const position = new Vector2(clientX, clientY);

    let velocity = new Vector2();

    let acceleration = new Vector2();

    if (type !== 'down') {
      velocity = Vector2.subtract(position, this.previousPosition);
      acceleration = Vector2.subtract(velocity, this.previousVelocity);
    }

    const pointerTargetOffset = Vector2.clone(this.pointerTargetOffset);

    this.previousPosition.equals(position);

    this.previousVelocity.equals(velocity);

    return {
      event,
      isTouch: false,
      target,
      pointerTargetOffset,
      position,
      velocity,
      acceleration,
      time: Date.now(),
      type,
    };
  }

  private eventHandlerMouseDown = (event: MouseEvent) => {
    if (this.isActive === false && this.config.condition(event, this) === true) {
      this.preventDefault(event);

      this.isActive = true;

      this.isTouch = false;

      this.startTime = Date.now();

      this.config.onEvent(event, this);

      this.updatePointerTargetOffset(event.clientX, event.clientY);

      const pointerEvent = this.createMousePointerDragEvent('down', event);

      this.history = [];

      this.updateHistory(pointerEvent);

      this.startingPointerDragEvent = pointerEvent;

      this.config.onStart(pointerEvent, this);

      this.previousPointerDragEvent = pointerEvent;
    }
  };

  private eventHandlerMouseMove = (event: MouseEvent) => {
    if (this.isActive === true) {
      this.preventDefault(event);

      this.config.onEvent(event, this);

      const pointerEvent = this.createMousePointerDragEvent('drag', event);

      this.updateHistory(pointerEvent);

      this.config.onDrag(pointerEvent, this);

      this.previousPointerDragEvent = pointerEvent;
    }
  };

  private eventHandlerMouseUp = (event: MouseEvent) => {
    if (this.isActive === true) {
      this.preventDefault(event);

      this.config.onEvent(event, this);

      const pointerEvent = this.createMousePointerDragEvent('up', event);

      this.updateHistory(pointerEvent);

      this.config.onEnd(pointerEvent, this);

      this.end();

      this.previousPointerDragEvent = pointerEvent;
    }
  };

  private eventHandlerMouseLeave = (event: MouseEvent) => {
    if (this.isActive === false) {
      this.preventDefault(event);

      this.config.onEvent(event, this);

      const pointerEvent = this.createMousePointerDragEvent('cancel', event);

      this.updateHistory(pointerEvent);

      this.config.onCancel(pointerEvent, this);

      this.end();

      this.previousPointerDragEvent = pointerEvent;
    }
  };

  private createTouchPointerDragEvent(
    type: PointerDragEventType,
    event: TouchEvent,
    touch: Touch,
  ): PointerDragEvent {
    const { identifier, clientX, clientY, target } = touch;

    const position = new Vector2(clientX, clientY);

    let velocity = new Vector2();
    let acceleration = new Vector2();

    if (type !== 'down') {
      velocity = Vector2.subtract(position, this.previousPosition);
      acceleration = Vector2.subtract(velocity, this.previousVelocity);
    }

    const pointerTargetOffset = Vector2.clone(this.pointerTargetOffset);

    this.previousPosition.equals(position);
    this.previousVelocity.equals(velocity);

    return {
      event,
      identifier,
      isTouch: true,
      target,
      pointerTargetOffset,
      position,
      velocity,
      acceleration,
      time: Date.now(),
      touch,
      type,
    };
  }

  private eventHandlerTouchStart = (event: TouchEvent) => {
    if (this.isActive === false && this.config.condition(event, this) === true) {
      this.preventDefault(event);

      this.isActive = true;
      this.isTouch = true;

      this.startTime = Date.now();

      this.config.onEvent(event, this);

      this.touchIdentifier = event.changedTouches[0].identifier;

      this.updatePointerTargetOffset(
        event.changedTouches[0].clientX,
        event.changedTouches[0].clientY,
      );

      const pointerEvent = this.createTouchPointerDragEvent('down', event, event.changedTouches[0]);

      this.history = [];
      this.updateHistory(pointerEvent);

      this.startingPointerDragEvent = pointerEvent;

      this.config.onStart(pointerEvent, this);

      this.previousPointerDragEvent = pointerEvent;
    }
  };

  private eventHandlerTouchMove = (event: TouchEvent) => {
    if (this.isActive === true) {
      this.preventDefault(event);

      this.config.onEvent(event, this);

      [...event.changedTouches].forEach(touch => {
        if (touch.identifier === this.touchIdentifier) {
          const pointerEvent = this.createTouchPointerDragEvent('drag', event, touch);

          this.updateHistory(pointerEvent);

          this.config.onDrag(pointerEvent, this);

          this.previousPointerDragEvent = pointerEvent;
        }
      });
    }
  };

  private eventHandlerTouchEnd = (event: TouchEvent) => {
    if (this.isActive === true) {
      this.preventDefault(event);

      this.config.onEvent(event, this);

      [...event.changedTouches].forEach(touch => {
        if (touch.identifier === this.touchIdentifier) {
          const pointerEvent = this.createTouchPointerDragEvent('up', event, touch);

          this.updateHistory(pointerEvent);

          this.config.onEnd(pointerEvent, this);

          this.previousPointerDragEvent = pointerEvent;
        }
      });

      this.end();
    }
  };

  private eventHandlerTouchCancel = (event: TouchEvent) => {
    if (this.isActive === true) {
      this.preventDefault(event);

      this.config.onEvent(event, this);

      [...event.changedTouches].forEach(touch => {
        if (touch.identifier === this.touchIdentifier) {
          const pointerEvent = this.createTouchPointerDragEvent('cancel', event, touch);

          this.updateHistory(pointerEvent);

          this.config.onCancel(pointerEvent, this);

          this.previousPointerDragEvent = pointerEvent;
        }
      });

      this.end();
    }
  };

  private end() {
    this.touchIdentifier = 0;

    this.isActive = false;
  }

  public listen() {
    let { target } = this.config;

    if (DOMUtil.isHTMLElement(target) === true) {
      target = target as HTMLElement;

      target.addEventListener('mousedown', this.eventHandlerMouseDown);
      window.addEventListener('mousemove', this.eventHandlerMouseMove);
      window.addEventListener('mouseup', this.eventHandlerMouseUp);
      document.body.addEventListener('mouseleave', this.eventHandlerMouseLeave);

      target.addEventListener('touchstart', this.eventHandlerTouchStart);
      window.addEventListener('touchmove', this.eventHandlerTouchMove);
      window.addEventListener('touchend', this.eventHandlerTouchEnd);
      window.addEventListener('touchcancel', this.eventHandlerTouchCancel);
    }
  }

  public stopListen() {
    let { target } = this.config;

    if (DOMUtil.isHTMLElement(target) === true) {
      target = target as HTMLElement;

      target.removeEventListener('mousedown', this.eventHandlerMouseDown);
      window.removeEventListener('mousemove', this.eventHandlerMouseMove);
      window.removeEventListener('mouseup', this.eventHandlerMouseUp);
      document.body.removeEventListener('mouseleave', this.eventHandlerMouseLeave);

      target.removeEventListener('touchstart', this.eventHandlerTouchStart);
      window.removeEventListener('touchmove', this.eventHandlerTouchMove);
      window.removeEventListener('touchend', this.eventHandlerTouchEnd);
      window.removeEventListener('touchcancel', this.eventHandlerTouchCancel);
    }
  }
}
